import { zonedTimeToUtc } from 'date-fns-tz';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import useTimeZone from '@swf/core/hooks/useTimeZone';
import { select } from '@swf/core/store';
import type { MyBet, SearchBetsQuery } from '@swf/generated/types/api/sports';
import { SortDirType, SortParamType } from '@swf/generated/types/api/sports';

import { useSportsMyBetsWidget } from '../model';

interface FetchBetsData {
  isFetching: boolean;
  fetchBets(payload: Partial<SearchBetsQuery>): void;
  fetchMoreBets(): void;
  bets: MyBet[];
  moreAvailable?: boolean;
  isErroneous: boolean;
}

const useFetchMyBets = (isSettled?: boolean, fromDate?: Date, toDate?: Date): FetchBetsData => {
  const { widgetSelect, widgetDispatch } = useSportsMyBetsWidget();
  const isErroneous = useSelector(widgetSelect.isErroneous);
  const isFetching = useSelector(widgetSelect.isFetching);
  const bets = useSelector(widgetSelect.bets);
  const moreAvailable = useSelector(widgetSelect.moreAvailable);
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const { timeZoneName } = useTimeZone();

  const fetchBets = useCallback(
    (payload: Partial<SearchBetsQuery> = {}) => {
      if (!isClientAuthenticated) {
        return;
      }

      const fromDateWithTimezoneDifference = fromDate && zonedTimeToUtc(fromDate, timeZoneName);
      const toDateWithTimezoneDifference = toDate && zonedTimeToUtc(toDate, timeZoneName);

      widgetDispatch.betsRequested({
        ...payload,
        isSettled,
        sortDir: isSettled ? SortDirType.DESC : SortDirType.ASC,
        sortParam: isSettled ? SortParamType.SETTLEMENT_DATE : SortParamType.CLOSEST_START_TIME,
        ...(fromDateWithTimezoneDifference && {
          settledAfter: fromDateWithTimezoneDifference.toISOString()
        }),
        ...(toDateWithTimezoneDifference && {
          settledBefore: toDateWithTimezoneDifference.toISOString()
        })
      });
    },
    [isClientAuthenticated, fromDate, timeZoneName, toDate, widgetDispatch, isSettled]
  );

  const fetchMoreBets = useCallback(() => {
    fetchBets();
  }, [fetchBets]);

  // Refetch on tab change (ensuring results reset)
  useEffect(() => {
    fetchBets({ fromRecord: 1 });
  }, [fetchBets]);

  return {
    bets,
    moreAvailable,
    isFetching,
    fetchBets,
    fetchMoreBets,
    isErroneous
  };
};

export default useFetchMyBets;
